import {
  AfterViewInit,
  Directive,
  DoCheck,
  Host,
  Input,
  OnInit,
  Optional,
  Renderer2,
  Self,
  ViewContainerRef,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import { PageProperties } from '../models/page-properties.model';

const customPaginatorArrowDisabled = 'custom-paginator-arrow-disabled';
const customPaginatorArrowEnabled = 'custom-paginator-arrow-enabled';

@Directive({
    selector: '[sedsPaginatorNumber]',
    standalone: false
})
export class SedsPaginatorNumberDirective implements DoCheck, AfterViewInit, OnInit {
  @Input() attrs?: PageProperties;
  private currentPage: number;
  readonly pageGapTxt: string[];
  private rangeStart = 0;
  private rangeEnd = 0;
  readonly buttons: MatButton[] = [];
  private readonly showTotalPages: number;
  private checkPage: number[];

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private readonly ViewContainer: ViewContainerRef,
    private readonly renderer: Renderer2
  ) {
    this.currentPage = 1;
    this.pageGapTxt = ['...'];
    this.showTotalPages = 3;
    this.checkPage = [0, 0, 0];
    this.matPag._intl.itemsPerPageLabel = 'Resultados por página';
    this.matPag._intl.nextPageLabel = 'Próxima página';
    this.matPag._intl.previousPageLabel = 'Página anterior';
    this.matPag._intl.firstPageLabel = 'Primeira página';
    this.matPag._intl.lastPageLabel = 'Última página';

    this.matPag.page.subscribe((paginator: PageEvent) => {
      this.currentPage = paginator.pageIndex;
      this.matPag.pageIndex = paginator.pageIndex;
      this.initPageRange();
    });
  }
  ngOnInit(): void {
    this.attrs = this.attrs || {};
    this.matPag._intl.getRangeLabel = (page: number, pageSize: number, length: number): string => {
      if (length === 0 || pageSize === 0) {
        return `0 de ${length}`;
      }
      length = Math.max(length, 0);
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return `${startIndex + 1} - ${endIndex} de ${length}`;
    };
  }

  ngDoCheck(): void {
    if (
      this.matPag?.length !== this.checkPage[0] ||
      this.matPag?.pageSize !== this.checkPage[1] ||
      this.matPag?.pageIndex !== this.checkPage[2]
    ) {
      const pageCount = this.matPag.getNumberOfPages();
      if (this.currentPage > pageCount && pageCount !== 0) {
        this.currentPage = 1;
        this.matPag.pageIndex = 0;
      }
      this.currentPage = this.matPag.pageIndex;
      this.initPageRange();
      this.checkPage = [this.matPag.length, this.matPag.pageSize, this.matPag.pageIndex];
    }
  }

  private handleClassManipulations(actionContainer: any, pageRange: any) {
    this.renderer.addClass(pageRange, 'custom-paginator-counter');
    this.renderer.addClass(actionContainer, 'custom-paginator-container');

    const nodeArray = actionContainer.childNodes;
    setTimeout(() => {
      for (const node of nodeArray) {
        if (
          node.nodeName === 'BUTTON' &&
          node?.classList &&
          /(^)-first$|-previows$|-next$|-last$/.test(node.classList.toString())
        ) {
          if (node.innerHTML.length > 100 && node.disabled) {
            this.renderer.addClass(node, customPaginatorArrowDisabled);
            this.renderer.removeClass(node, customPaginatorArrowEnabled);
          } else if (node.innerHTML.length > 100 && !node.disabled) {
            this.renderer.addClass(node, customPaginatorArrowEnabled);
            this.renderer.removeClass(node, customPaginatorArrowDisabled);
          }
          if (this.matPag.disabled) {
            this.renderer.setAttribute(node, 'disabled', 'true');
            this.renderer.addClass(node, customPaginatorArrowDisabled);
          } else {
            this.renderer.removeAttribute(node, 'disabled');
            this.renderer.removeClass(node, customPaginatorArrowDisabled);
          }
        }
      }
    });
  }

  private shouldRenderButton(index: number, page: number, pageDifference: number, totalPages: number): boolean {
    return (
      (index < page && this.currentPage <= this.showTotalPages) ||
      (index >= this.rangeStart && index <= this.rangeEnd) ||
      (this.currentPage > pageDifference && index >= pageDifference) ||
      totalPages < this.showTotalPages + page
    );
  }

  private renderDotsButton(index: number, dots: boolean[], actionContainer: any, nextPageNode: any): boolean[] {
    if (index > this.rangeEnd && !dots[0]) {
      this.renderer.insertBefore(
        actionContainer,
        this.createButton(this.pageGapTxt[0], this.matPag.pageIndex),
        nextPageNode
      );
      dots[0] = true;
    } else if (index < this.rangeEnd && !dots[1]) {
      this.renderer.insertBefore(
        actionContainer,
        this.createButton(this.pageGapTxt[1], this.matPag.pageIndex),
        nextPageNode
      );
      dots[1] = true;
    }
    return dots;
  }

  private renderButtonsConditions(totalPages: number, actionContainer: any, nextPageNode: any) {
    let dots: boolean[] = [false, false];

    if (totalPages > 0) {
      this.renderer.insertBefore(actionContainer, this.createButton('0', this.matPag.pageIndex), nextPageNode);
    }

    const page = this.showTotalPages + 2;
    const pageDifference = totalPages - page;
    const startIndex = Math.max(this.currentPage - this.showTotalPages - 2, 1);

    for (let index = startIndex; index < totalPages - 1; index = index + 1) {
      if (this.shouldRenderButton(index, page, pageDifference, totalPages)) {
        this.renderer.insertBefore(actionContainer, this.createButton(`${index}`, this.matPag.pageIndex), nextPageNode);
      } else {
        dots = this.renderDotsButton(index, dots, actionContainer, nextPageNode);
        if (dots[0]) break;
      }
    }

    if (totalPages > 1) {
      this.renderer.insertBefore(
        actionContainer,
        this.createButton(`${totalPages - 1}`, this.matPag.pageIndex),
        nextPageNode
      );
    }
  }

  private buildPageNumbers() {
    const totalPages = this.matPag.getNumberOfPages();
    const actionContainer = this.ViewContainer.element.nativeElement.querySelector(
      'div.mat-mdc-paginator-range-actions'
    );
    const nextPageNode = this.ViewContainer.element.nativeElement.querySelector(
      'button.mat-mdc-paginator-navigation-next'
    );
    const pageRange = this.ViewContainer.element.nativeElement.querySelector('div.mat-mdc-paginator-range-label');

    if (this.buttons.length > 0) {
      this.buttons.forEach(button => {
        this.renderer.removeChild(actionContainer, button);
      });
    }

    this.handleClassManipulations(actionContainer, pageRange);
    this.renderButtonsConditions(totalPages, actionContainer, nextPageNode);
  }

  private createButton(index: string, pageIndex: number): MatButton {
    const linkBtn: MatButton = this.renderer.createElement('button');
    this.renderer.setAttribute(linkBtn, 'class', 'custom-paginator-page');
    this.renderer.addClass(linkBtn, 'custom-paginator-page-enabled');
    if (index === this.pageGapTxt[0] || index === this.pageGapTxt[1]) {
      this.renderer.addClass(linkBtn, customPaginatorArrowEnabled);
    }
    const pagingTxt = isNaN(+index) ? this.pageGapTxt[0] : +index + 1;
    const text = this.renderer.createText(pagingTxt + '');
    this.renderer.setAttribute(linkBtn, 'aria-label', `Página ${pagingTxt}`);
    this.renderer.addClass(linkBtn, 'mat-custom-page');
    switch (index) {
      case `${pageIndex}`:
        this.renderer.setAttribute(linkBtn, 'disabled', 'disabled');
        this.renderer.removeClass(linkBtn, 'custom-paginator-page-enabled');
        this.renderer.addClass(linkBtn, 'custom-paginator-page-disabled');
        break;
      case this.pageGapTxt[0]:
        this.renderer.listen(linkBtn, 'click', () => {
          this.switchPage(
            this.currentPage < this.showTotalPages + 1
              ? this.showTotalPages + 2
              : this.currentPage + this.showTotalPages - 1
          );
        });
        break;
      case this.pageGapTxt[1]:
        this.renderer.listen(linkBtn, 'click', () => {
          this.switchPage(
            this.currentPage > this.matPag.getNumberOfPages() - this.showTotalPages - 2
              ? this.matPag.getNumberOfPages() - this.showTotalPages - 3
              : this.currentPage - this.showTotalPages + 1
          );
        });
        break;
      default:
        this.renderer.listen(linkBtn, 'click', () => {
          this.switchPage(+index);
        });
        break;
    }
    this.renderer.appendChild(linkBtn, text);

    this.buttons.push(linkBtn);
    return linkBtn;
  }

  private initPageRange(): void {
    this.rangeStart = this.currentPage - this.showTotalPages / 2;
    this.rangeEnd = this.currentPage + this.showTotalPages / 2;
    if (this.attrs?.hideRange) {
      const pageRange = this.ViewContainer.element.nativeElement.querySelector('div.mat-mdc-paginator-range-label');
      this.renderer.setAttribute(pageRange, 'hidden', 'true');
    }
    if (!this.attrs?.hideNumbers) {
      this.buildPageNumbers();
    }
  }

  private switchPage(index: number): void {
    this.matPag.pageIndex = index;
    this.matPag.page.emit({
      previousPageIndex: this.currentPage,
      pageIndex: index,
      pageSize: this.matPag.pageSize,
      length: this.matPag.length,
    });
    this.currentPage = index;
    this.initPageRange();
  }

  public ngAfterViewInit(): void {
    this.rangeStart = 0;
    this.rangeEnd = this.showTotalPages - 1;
    this.initPageRange();
  }
}
